(() => {
  const config = window.WazupTrackingConfig || {};
  const seenSections = new Set();
  const seenDepths = new Set();
  const gaQueue = [];
  let gaReady = false;
  let gaScheduled = false;

  const clean = (value, fallback = 'unknown') => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    return (text || fallback).slice(0, 100);
  };

  const pageContext = () => ({
    page_template: clean(config.template),
    page_type: clean(config.pageType),
    page_path: window.location.pathname,
    page_title: clean(document.title),
  });

  const publishToShopify = (name, detail) => {
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
    } catch (error) {
      console.debug('[Wazup tracking] Shopify publish skipped', error);
    }
  };

  const sendToGa = (name, detail) => {
    if (!config.ga4Id) return;
    if (!gaReady) {
      gaQueue.push([name, detail]);
      return;
    }
    if (!analyticsAllowed()) return;
    window.gtag?.('event', name, { ...detail, send_to: config.ga4Id });
  };

  const track = (name, detail = {}) => {
    const payload = { ...pageContext(), ...detail };
    publishToShopify(name, payload);
    sendToGa(name, payload);
  };

  const analyticsAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  };

  const enableGa = () => {
    if (gaReady || !config.ga4Id || !analyticsAllowed()) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', config.ga4Id, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.ga4Id)}`;
    document.head.appendChild(script);

    gaReady = true;
    while (gaQueue.length) {
      const [name, detail] = gaQueue.shift();
      sendToGa(name, detail);
    }
  };

  const scheduleGa = () => {
    if (gaReady || gaScheduled || !config.ga4Id || !analyticsAllowed()) return;
    gaScheduled = true;

    const load = () => {
      gaScheduled = false;
      enableGa();
    };

    window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: 1000 });
      } else {
        load();
      }
    }, 1500);
  };

  const sectionInfo = (element) => {
    const wrapper = element.closest('[id^="shopify-section-"]') || element;
    const heading = wrapper.querySelector('h1, h2, h3, [role="heading"]');
    const rawSectionId = wrapper.id.replace(/^shopify-section-/, '');
    const sectionType = Array.from(wrapper.classList)
      .find((className) => className.startsWith('shopify-section-'));
    const sectionIdType = rawSectionId.split('__').pop();

    return {
      section_id: clean(rawSectionId),
      section_type: clean(
        sectionType?.replace(/^shopify-section-/, '') || sectionIdType,
      ),
      section_heading: clean(heading?.textContent),
    };
  };

  const observeSections = () => {
    const sections = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.45) return;
        const info = sectionInfo(entry.target);
        if (seenSections.has(info.section_id)) return;
        seenSections.add(info.section_id);
        track('wazup_section_view', info);
        observer.unobserve(entry.target);
      });
    }, { threshold: [0.45] });

    sections.forEach((section) => observer.observe(section));
  };

  const trackClicks = () => {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('a, button, summary');
      if (!target) return;

      const info = sectionInfo(target);
      const href = target.getAttribute('href') || '';
      const label = clean(target.getAttribute('aria-label') || target.textContent || target.title);
      const detail = {
        ...info,
        element_type: target.tagName.toLowerCase(),
        element_label: label,
        destination_path: href.startsWith('/') ? href.split('?')[0] : '',
      };

      track('wazup_section_click', detail);

      if (href.includes('/products/')) {
        track('wazup_product_card_click', detail);
      }
      if (/size\s*(guide|chart)|fit\s*guide/i.test(label)) {
        track('wazup_size_guide_open', detail);
      }
    }, { passive: true });
  };


  // Own-page signals only. Never read the cross-origin ShipAny frame or form values.
  const trackCheckoutFunnel = () => {
    const root = document.documentElement;
    if (root.hasAttribute('data-wazup-funnel-version')) return;
    root.setAttribute('data-wazup-funnel-version', '20260918');
    let pendingClickForm = null;
    let pendingClickTimer;
    let shipanyWasShown = false;
    let checkScheduled = false;

    const emit = (name, detail = {}) => {
      // DOM-only QA signal: a fixed event name, not a server delivery receipt.
      root.setAttribute('data-wazup-funnel-last-observed', name);
      root.setAttribute('data-wazup-funnel-consent', analyticsAllowed() ? 'allowed' : 'not_allowed');
      if (!analyticsAllowed()) return;
      const payload = {
        page_type: clean(config.pageType),
        funnel_version: '20260918',
        ...detail,
      };
      publishToShopify(name, payload);
      sendToGa(name, payload);
    };

    const checkoutControl = (element) => {
      const target = element?.closest?.('button, input, a');
      if (!target || target.disabled) return null;
      if (target.name === 'checkout' && target.matches('button, input')) return target;
      if (!target.matches('a[href]')) return null;
      try {
        const url = new URL(target.getAttribute('href'), window.location.href);
        return url.origin === window.location.origin
          && /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/checkouts?(?:\/|$)/i.test(url.pathname)
          ? target : null;
      } catch {
        return null;
      }
    };

    // Capture phase runs before the app's normal bubbling checkout interception.
    document.addEventListener('click', (event) => {
      if (event.isTrusted === false) return;
      const control = checkoutControl(event.target);
      if (!control) return;
      pendingClickForm = control.form || null;
      window.clearTimeout(pendingClickTimer);
      // A click and its synchronous form submit are one intent. A later submit is new.
      pendingClickTimer = window.setTimeout(() => { pendingClickForm = null; }, 0);
      emit('wazup_checkout_intent', { interaction_type: 'click' });
    }, { capture: true, passive: true });

    document.addEventListener('submit', (event) => {
      if (event.isTrusted === false || !(event.target instanceof HTMLFormElement)) return;
      if (!checkoutControl(event.submitter)) return;
      if (pendingClickForm === event.target) {
        pendingClickForm = null;
        return;
      }
      emit('wazup_checkout_intent', { interaction_type: 'submit' });
    }, { capture: true, passive: true });

    const checkShipany = () => {
      checkScheduled = false;
      const dialog = document.getElementById('shipany-dialog');
      const frame = dialog?.querySelector('iframe#acd_iframe');
      let matchesFrame = false;
      if (frame) {
        try {
          const url = new URL(frame.getAttribute('src'), window.location.href);
          matchesFrame = url.protocol === 'https:'
            && ['pickup-location-app.shipany.io', 'pickup-location-app.shipany.click'].includes(url.hostname);
        } catch { /* An absent or malformed frame source is not evidence of ShipAny. */ }
      }
      const visible = Boolean(matchesFrame && dialog.tagName === 'DIALOG' && dialog.open
        && dialog.getClientRects().length && window.getComputedStyle(dialog).visibility !== 'hidden');
      root.setAttribute('data-wazup-shipany-state', visible ? 'shown' : 'not_shown');
      if (visible === shipanyWasShown) return;
      shipanyWasShown = visible;
      emit(visible ? 'wazup_shipany_shown' : 'wazup_shipany_closed', {
        observed_state: visible ? 'shown' : 'closed',
      });
    };
    const scheduleCheck = () => {
      if (checkScheduled) return;
      checkScheduled = true;
      window.requestAnimationFrame(checkShipany);
    };
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(scheduleCheck);
      observer.observe(document.documentElement, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['open', 'hidden', 'style', 'class', 'src'],
      });
      // Native close does not bubble; capture it without changing the dialog action.
      document.addEventListener('close', scheduleCheck, { capture: true, passive: true });
      checkShipany();
    }
  };

  const trackForms = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isNewsletter = form.matches('[id*="newsletter" i], [class*="newsletter" i]')
        || form.querySelector('input[name="contact[email]"]');
      if (isNewsletter) {
        track('wazup_newsletter_submit', sectionInfo(form));
      }
    });

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      if (!input.closest('variant-selects, variant-radios, .product-form__input')) return;

      track('wazup_variant_selected', {
        ...sectionInfo(input),
        option_name: clean(input.name),
        option_value: clean(input.value),
      });
    });
  };

  const trackScrollDepth = () => {
    const thresholds = [25, 50, 75, 90];
    let scheduled = false;

    const measure = () => {
      scheduled = false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depth = Math.round((window.scrollY / scrollable) * 100);

      thresholds.forEach((threshold) => {
        if (depth < threshold || seenDepths.has(threshold)) return;
        seenDepths.add(threshold);
        track('wazup_scroll_depth', { scroll_depth: threshold });
      });
    };

    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(measure);
    }, { passive: true });
  };

  const trackCampaignLanding = () => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return;

    track('wazup_campaign_landing', {
      campaign_source: clean(source),
      campaign_medium: clean(params.get('utm_medium')),
      campaign_name: clean(params.get('utm_campaign')),
      campaign_content: clean(params.get('utm_content')),
    });
  };

  const init = () => {
    scheduleGa();
    document.addEventListener('visitorConsentCollected', scheduleGa);
    observeSections();
    trackClicks();
    trackCheckoutFunnel();
    trackForms();
    trackScrollDepth();
    trackCampaignLanding();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
